/**
 * 通告値の取込のファイル 1 つを受け取り先 (署名つき URL) へ上げる、 他社と自社の取込で共通の手順。
 * 手順: 発行 RPC → `file.size > maxContentBytes` なら中止 → 発行 URL へ PUT (response の
 * method / headers をそのまま使う)。 発行 RPC だけが取込の側ごとに違う。
 */

/** 発行 response の上限 (max_content_bytes) を超えるファイルを選んだ。 PUT も取込もしない。 */
export class NoticeImportFileTooLargeError extends Error {
  readonly maxContentBytes: bigint
  constructor(maxContentBytes: bigint) {
    super('notice import file too large')
    this.name = 'NoticeImportFileTooLargeError'
    this.maxContentBytes = maxContentBytes
  }
}

/** 発行 URL への PUT が失敗した (network error / !res.ok)。 */
export class NoticeImportPutFailedError extends Error {
  constructor() {
    super('notice import PUT failed')
    this.name = 'NoticeImportPutFailedError'
  }
}

/** 発行 RPC の response のうち、 アップロードに使う部分。 */
export type IssuedNoticeUpload = {
  uploadToken: string
  maxContentBytes: bigint
  upload?: {
    url: string
    method: string
    headers: Record<string, string>
  }
}

/**
 * ファイル 1 つを上げて、 取込の token を返す。 複数のファイルを同時に上げてよい (呼び出しごとに独立)。
 * organization_id は BFF が eauth から補完するため frontend には存在しない。
 */
export async function uploadNoticeImportFile(
  file: File,
  issue: (fileName: string) => Promise<IssuedNoticeUpload>,
): Promise<string> {
  const issued = await issue(file.name)
  if (BigInt(file.size) > issued.maxContentBytes) {
    throw new NoticeImportFileTooLargeError(issued.maxContentBytes)
  }
  const upload = issued.upload
  // backend / BFF は upload を必ず返す契約。 未設定は契約違反で、 再試行で直る PUT 失敗とは区別する
  if (upload === undefined) {
    throw new Error('issued upload request is missing')
  }
  const res = await fetch(upload.url, {
    method: upload.method,
    headers: upload.headers,
    body: file,
  }).catch(() => {
    throw new NoticeImportPutFailedError()
  })
  if (!res.ok) throw new NoticeImportPutFailedError()
  return issued.uploadToken
}
